import { PresetInfo } from '@/components/PresetCard';

export enum PresetCategory {
  PURE_FREQUENCIES = 'pure-frequencies',
  AMBIENT_FREQUENCIES = 'ambient-frequencies',
  SOUNDSCAPE_FREQUENCIES = 'soundscape-frequencies',
  EXPERIMENTAL = 'experimental'
}

export interface PresetSection {
  category: PresetCategory;
  title: string;
  description: string;
  presets: PresetInfo[];
}

// Pure frequency presets (no ambient sounds)
const FREQUENCY_PRESETS: PresetInfo[] = [
  {
    id: 'focus',
    name: 'Focus',
    frequency: 15,
    duration: 20,
    description: 'Beta waves for enhanced focus and concentration',
    color: '#4C8DAA'
  },
  {
    id: 'relax',
    name: 'Relax',
    frequency: 8,
    duration: 20,
    description: 'Alpha waves for relaxation and stress relief',
    color: '#3A95C9'
  },
  {
    id: 'meditate',
    name: 'Meditate',
    frequency: 7,
    duration: 20,
    description: 'Theta waves for deep meditation',
    color: '#4CAF50'
  },
  {
    id: 'sleep',
    name: 'Sleep',
    frequency: 3,
    duration: 20,
    description: 'Delta waves for deep sleep',
    color: '#9575CD'
  },
  {
    id: 'gamma',
    name: 'Gamma',
    frequency: 40,
    duration: 20,
    description: 'Gamma waves for heightened awareness and cognitive processing',
    color: '#F97316'
  }
];

// Presets with ambient sounds
const AMBIENT_PRESETS: PresetInfo[] = [
  {
    id: 'rain-focus',
    name: 'Rainy Focus',
    frequency: 12,
    duration: 20,
    description: 'Gentle rain sounds with beta waves for focused work',
    color: '#4C8DAA',
    ambientSounds: [{ id: 'rain', volume: 50 }]
  },
  {
    id: 'ocean-calm',
    name: 'Ocean Meditation',
    frequency: 8,
    duration: 20,
    description: 'Calming ocean waves with alpha waves for relaxation',
    color: '#3A95C9',
    ambientSounds: [{ id: 'ocean', volume: 60 }]
  },
  {
    id: 'forest-mind',
    name: 'Forest Mindfulness',
    frequency: 7,
    duration: 20,
    description: 'Forest ambience with theta waves for deep meditation',
    color: '#4CAF50',
    ambientSounds: [{ id: 'forest', volume: 50 }]
  },
  {
    id: 'wind-sleep',
    name: 'Wind Lullaby',
    frequency: 3,
    duration: 20,
    description: 'Gentle wind sounds with delta waves for better sleep',
    color: '#9575CD',
    ambientSounds: [{ id: 'wind', volume: 45 }]
  }
];

// Presets with soundscapes
const SOUNDSCAPE_PRESETS: PresetInfo[] = [
  {
    id: 'space-focus',
    name: 'Space Focus',
    frequency: 12,
    duration: 20,
    description: 'Space atmosphere with beta waves for deep focus',
    color: '#6B4EFF',
    ambientSounds: [{ id: 'space', volume: 40 }]
  },
  {
    id: 'atmospheric-meditation',
    name: 'Atmospheric Meditation',
    frequency: 7,
    duration: 20,
    description: 'Atmospheric soundscape with theta waves for meditation',
    color: '#9B87F5',
    ambientSounds: [{ id: 'atmospheric', volume: 35 }]
  },
  {
    id: 'tribute-relax',
    name: 'Tribute Relaxation',
    frequency: 8,
    duration: 20,
    description: 'Tribute wave with alpha waves for relaxation',
    color: '#7C4DFF',
    ambientSounds: [{ id: 'tribute', volume: 35 }]
  }
];

const EXPERIMENTAL_PRESET: PresetInfo = {
  id: 'harmonic-fusion',
  name: 'Harmonic Fusion',
  frequency: 7.83,
  duration: 25,
  description: 'Schumann resonance with mixed waveforms and forest/rain blend',
  color: '#D946EF',
  ambientSounds: [
    { id: 'forest', volume: 30 },
    { id: 'rain', volume: 20 }
  ],
  advancedSettings: {
    leftFrequency: 7.83,
    rightFrequency: 7.83,
    carrierFrequency: 220,
    leftWaveform: 'sine',
    rightWaveform: 'triangle'
  }
};

const LUCKY_PRESET: PresetInfo = {
  id: 'lucky',
  name: "I'm Feeling Lucky",
  frequency: 0,
  duration: 0,
  description: 'Surprise yourself with random frequencies and durations',
  color: '#D946EF',
  isLucky: true
};

export const PRESET_SECTIONS: PresetSection[] = [
  {
    category: PresetCategory.PURE_FREQUENCIES,
    title: 'Pure Frequencies',
    description: 'Simple binaural beats without ambient sounds',
    presets: FREQUENCY_PRESETS
  },
  {
    category: PresetCategory.AMBIENT_FREQUENCIES,
    title: 'Ambient Frequencies',
    description: 'Binaural beats combined with natural ambient sounds',
    presets: AMBIENT_PRESETS
  },
  {
    category: PresetCategory.SOUNDSCAPE_FREQUENCIES,
    title: 'Soundscape Frequencies',
    description: 'Binaural beats paired with atmospheric soundscapes',
    presets: SOUNDSCAPE_PRESETS
  },
  {
    category: PresetCategory.EXPERIMENTAL,
    title: 'Experimental',
    description: 'Unique combinations and special presets',
    presets: [EXPERIMENTAL_PRESET, LUCKY_PRESET]
  }
];

// Flattened array of all presets for easy access
export const ALL_PRESETS: PresetInfo[] = PRESET_SECTIONS.flatMap(section => section.presets); 